/**
 * Onglet Test de Commandes pour l'administration
 * @module admin/tabs/CommandTestTab
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCommandTest } from '../hooks/useCommandTest';
import AdminResult from '../components/AdminResult';
import UploadService from '../../../services/UploadService';

/**
 * Composant onglet Test de Commandes
 * @returns {JSX.Element}
 */
const CommandTestTab = () => {
  const {
    commands,
    selectedCommand,
    commandArgs,
    testOptions,
    isLoading,
    isResetting,
    result,
    error,
    history,
    handleCommandChange,
    handleArgsChange,
    handleOptionChange,
    handleTestCommand,
    handleResetLimit,
    replayTest,
    deleteHistoryEntry,
    clearHistory,
  } = useCommandTest();

  const audioRef = useRef(null);
  const [isSoundPlaying, setIsSoundPlaying] = useState(false);
  const [soundError, setSoundError] = useState(null);

  const handleCommandSelectChange = (e) => {
    handleCommandChange(e.target.value);
  };

  const selectedCommandInfo = useMemo(
    () => commands.find((cmd) => cmd.name === selectedCommand),
    [commands, selectedCommand]
  );

  const stopSound = useCallback(() => {
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (_) {
        // ignore
      }
      audioRef.current = null;
    }
    setIsSoundPlaying(false);
  }, []);

  const playSound = useCallback(async () => {
    if (!selectedCommandInfo?.soundUrl) return;

    setSoundError(null);
    stopSound();

    const fullUrl = UploadService.getFullUrl(selectedCommandInfo.soundUrl);
    const volume =
      selectedCommandInfo.soundVolume !== undefined
        ? selectedCommandInfo.soundVolume
        : 0.5;

    try {
      const audio = new Audio(fullUrl);
      audio.volume = Math.max(0, Math.min(1, Number(volume) || 0.5));
      audioRef.current = audio;

      audio.onended = () => {
        setIsSoundPlaying(false);
        audioRef.current = null;
      };
      audio.onerror = () => {
        setIsSoundPlaying(false);
        audioRef.current = null;
        setSoundError("Impossible de lire le son (URL invalide ou fichier manquant).");
      };

      setIsSoundPlaying(true);
      await audio.play();
    } catch (err) {
      setIsSoundPlaying(false);
      audioRef.current = null;
      setSoundError(
        err?.message ||
          "Impossible de lire le son (peut nécessiter une interaction utilisateur)."
      );
    }
  }, [selectedCommandInfo, stopSound]);

  // Stop sound when changing command or unmounting
  useEffect(() => {
    stopSound();
    setSoundError(null);
  }, [selectedCommand, stopSound]);

  return (
    <>
      <section className="admin-section">
        <h2>🧪 Tester une commande</h2>
        <p className="admin-info">
          Testez l'exécution des commandes hors live pour vérifier qu'elles
          fonctionnent correctement.
        </p>

        <div className="admin-form">
          <div className="admin-field">
            <label htmlFor="commandName">Commande</label>
            <select
              id="commandName"
              value={selectedCommand}
              onChange={handleCommandSelectChange}
            >
              <option value="">Sélectionner une commande...</option>
              {commands.map((cmd) => (
                <option key={cmd.name} value={cmd.name}>
                  !{cmd.name} - {cmd.description || 'Sans description'}
                </option>
              ))}
            </select>
          </div>

          {selectedCommandInfo && (
            <div className="admin-info-box">
              <p>
                <strong>Description:</strong> {selectedCommandInfo.description || 'Aucune description'}
              </p>
              <p>
                <strong>Usage:</strong> <code>{selectedCommandInfo.usage || `!${selectedCommandInfo.name}`}</code>
              </p>
              {selectedCommandInfo.cooldown > 0 && (
                <p>
                  <strong>Cooldown:</strong> {selectedCommandInfo.cooldown}s
                </p>
              )}
              {selectedCommandInfo.permissions && selectedCommandInfo.permissions.length > 0 && (
                <p>
                  <strong>Permissions:</strong>{' '}
                  {selectedCommandInfo.permissions.join(', ')}
                </p>
              )}
              {selectedCommandInfo.assignedTo && (
                <p>
                  <strong>Réservée à:</strong> @{selectedCommandInfo.assignedTo}
                </p>
              )}
              {selectedCommandInfo.limitPerStream > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <p style={{ margin: 0 }}>
                    <strong>Limite/stream:</strong> {selectedCommandInfo.limitPerStream}
                  </p>
                  <button
                    type="button"
                    className="admin-button admin-button-secondary"
                    onClick={handleResetLimit}
                    disabled={isResetting}
                    style={{ fontSize: '0.9em', padding: '0.4rem 0.8rem' }}
                  >
                    {isResetting ? 'Réinitialisation...' : '🔄 Réinitialiser la limite'}
                  </button>
                </div>
              ) : (
                <p>
                  <strong>Limite/stream:</strong> Aucune limite
                </p>
              )}
              {selectedCommandInfo.soundUrl && (
                <>
                  <p>
                    <strong>Son:</strong> <code>{selectedCommandInfo.soundUrl}</code>
                  </p>
                  <p>
                    <strong>Volume:</strong>{' '}
                    {selectedCommandInfo.soundVolume !== undefined
                      ? selectedCommandInfo.soundVolume
                      : 0.5}
                  </p>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="admin-button admin-button-secondary"
                      onClick={isSoundPlaying ? stopSound : playSound}
                    >
                      {isSoundPlaying ? 'Stop' : 'Lire le son'}
                    </button>
                  </div>
                  {soundError && (
                    <p style={{ color: 'var(--admin-danger)', marginTop: '0.75rem' }}>
                      {soundError}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="admin-field">
            <label htmlFor="commandArgs">Arguments (optionnel)</label>
            <input
              id="commandArgs"
              type="text"
              value={commandArgs}
              onChange={(e) => handleArgsChange(e.target.value)}
              placeholder="Ex: arg1 arg2 arg3"
            />
          </div>

          <div className="admin-field-group">
            <h3>Options de simulation</h3>

            <div className="admin-field">
              <label htmlFor="testUsername">Nom d'utilisateur</label>
              <input
                id="testUsername"
                type="text"
                value={testOptions.username}
                onChange={(e) => handleOptionChange('username', e.target.value)}
                placeholder="test_user"
              />
            </div>

            <div className="admin-field">
              <label htmlFor="testDisplayName">Nom d'affichage</label>
              <input
                id="testDisplayName"
                type="text"
                value={testOptions.displayName}
                onChange={(e) =>
                  handleOptionChange('displayName', e.target.value)
                }
                placeholder="Test User"
              />
            </div>

            <div className="admin-field">
              <label>
                <input
                  type="checkbox"
                  checked={testOptions.isBroadcaster}
                  onChange={(e) =>
                    handleOptionChange('isBroadcaster', e.target.checked)
                  }
                />
                Broadcaster
              </label>
            </div>

            <div className="admin-field">
              <label>
                <input
                  type="checkbox"
                  checked={testOptions.isMod}
                  onChange={(e) =>
                    handleOptionChange('isMod', e.target.checked)
                  }
                />
                Modérateur
              </label>
            </div>

            <div className="admin-field">
              <label>
                <input
                  type="checkbox"
                  checked={testOptions.isVIP}
                  onChange={(e) =>
                    handleOptionChange('isVIP', e.target.checked)
                  }
                />
                VIP
              </label>
            </div>

            <div className="admin-field">
              <label>
                <input
                  type="checkbox"
                  checked={testOptions.isSubscriber}
                  onChange={(e) =>
                    handleOptionChange('isSubscriber', e.target.checked)
                  }
                />
                Abonné
              </label>
            </div>
          </div>

          <button
            className="admin-button admin-button-primary"
            onClick={handleTestCommand}
            disabled={isLoading || !selectedCommand}
          >
            {isLoading ? 'Exécution...' : 'Tester la commande'}
          </button>
        </div>

        {result && (
          <AdminResult
            isSuccess={result.success}
            title={result.success ? 'Résultat' : 'Erreur'}
          >
            {result.result && (
              <div>
                {result.result.message && (
                  <p>
                    <strong>Message:</strong> {result.result.message}
                  </p>
                )}
                {result.result.success !== undefined && (
                  <p>
                    <strong>Succès:</strong>{' '}
                    {result.result.success ? '✅' : '❌'}
                  </p>
                )}
              </div>
            )}
            {result.limitInfo && (
              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--admin-bg-secondary)', borderRadius: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <p style={{ margin: 0 }}>
                    <strong>Limite d'utilisation:</strong>
                  </p>
                  {selectedCommand && (
                    <button
                      type="button"
                      className="admin-button admin-button-secondary"
                      onClick={handleResetLimit}
                      disabled={isResetting}
                      style={{ fontSize: '0.85em', padding: '0.3rem 0.6rem' }}
                    >
                      {isResetting ? 'Réinitialisation...' : '🔄 Réinitialiser'}
                    </button>
                  )}
                </div>
                <p>
                  <strong>Utilisations:</strong> {result.limitInfo.count}/{result.limitInfo.limit}{' '}
                  {result.limitInfo.allowed ? (
                    <span style={{ color: 'var(--admin-success)' }}>✅ Disponible</span>
                  ) : (
                    <span style={{ color: 'var(--admin-danger)' }}>⛔ Limite atteinte</span>
                  )}
                </p>
                {result.limitInfo.streamStartedAt && (
                  <p style={{ marginTop: '0.5rem', fontSize: '0.9em', color: 'var(--admin-text-secondary)' }}>
                    <strong>Session stream:</strong> {result.limitInfo.streamStartedAt}
                  </p>
                )}
              </div>
            )}
            {result.context && (
              <div className="admin-output">
                <strong>Contexte de test:</strong>
                <pre>{JSON.stringify(result.context, null, 2)}</pre>
              </div>
            )}
            {result.error && (
              <div className="admin-warnings">
                <strong>Erreur:</strong>
                <pre>{result.error}</pre>
              </div>
            )}
          </AdminResult>
        )}

        {error && (
          <AdminResult isSuccess={false} title="Erreur" message={error} />
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>📜 Historique des tests</h2>
          {history.length > 0 && (
            <button
              className="admin-button admin-button-secondary"
              onClick={clearHistory}
            >
              Effacer l'historique
            </button>
          )}
        </div>

        {history.length === 0 ? (
          <p className="admin-info">Aucun test dans l'historique.</p>
        ) : (
          <div className="admin-history">
            {history.map((entry) => (
              <div key={entry.id} className="admin-history-entry">
                <div className="admin-history-header">
                  <span className="admin-history-event">
                    !{entry.commandName}
                    {entry.args.length > 0 && ` ${entry.args.join(' ')}`}
                  </span>
                  <span className="admin-history-time">
                    {new Date(entry.timestamp).toLocaleString('fr-FR')}
                  </span>
                </div>
                <div className="admin-history-options">
                  <p>
                    <strong>Utilisateur:</strong> {entry.options.username}
                    {entry.options.isBroadcaster && ' (Broadcaster)'}
                    {entry.options.isMod && ' (Mod)'}
                    {entry.options.isVIP && ' (VIP)'}
                    {entry.options.isSubscriber && ' (Sub)'}
                  </p>
                  {(() => {
                    const cmd = commands.find((c) => c.name === entry.commandName);
                    if (!cmd?.soundUrl) return null;
                    return (
                      <p>
                        <strong>Son:</strong> <code>{cmd.soundUrl}</code>
                      </p>
                    );
                  })()}
                  {entry.result && entry.result.result && (
                    <p>
                      <strong>Résultat:</strong>{' '}
                      {entry.result.result.message || 'Aucun message'}
                    </p>
                  )}
                  {entry.result && entry.result.limitInfo && (
                    <p>
                      <strong>Limite:</strong> {entry.result.limitInfo.count}/{entry.result.limitInfo.limit}{' '}
                      {entry.result.limitInfo.allowed ? '✅' : '⛔'}
                    </p>
                  )}
                </div>
                <div className="admin-history-actions">
                  <button
                    className="admin-button admin-button-small"
                    onClick={() => replayTest(entry)}
                  >
                    Rejouer
                  </button>
                  <button
                    className="admin-button admin-button-small admin-button-danger"
                    onClick={() => deleteHistoryEntry(entry.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default CommandTestTab;
